import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Input, Output, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { IonToggle, ToggleCustomEvent } from '@ionic/angular/standalone';

import { ButtonComponent } from '../../components/buttons/button/button.component';
import { SvgIconComponent } from '../../components/icons/svg-icon/svg-icon.component';
import { CheckboxGroupChipsComponent } from '../../components/inputs/checkbox-group-chips/checkbox-group-chips.component';
import { LoadingService } from '../../services/loading/loading.service';
import { ToastService } from '../../services/toast/toast.service';
import { ProUserService } from '../../services/users/pro/pro.service';
import { DaysOfTheWeekType, ProSchedule, ProScheduleFields } from '../../types';

@Component({
    selector: 'lib-guard-availability-settings',
    imports: [CommonModule, IonToggle, FormsModule, ButtonComponent, SvgIconComponent, CheckboxGroupChipsComponent],
    templateUrl: './guard-availability-settings.component.html',
    styleUrl: './guard-availability-settings.component.css'
})
export class GuardAvailabilitySettingsComponent {
    /* eslint-disable */
    loadingService = inject(LoadingService);
    proUserService = inject(ProUserService);
    toastService = inject(ToastService);

    @Input() buttonLabel: string = 'Save changes';
    @Input() showSuccessToast: boolean = true;

    @Output() onSaveSuccess = new EventEmitter();

    toggleAllShifts: WritableSignal<boolean> = signal(true);
    toggleExclusion: WritableSignal<boolean> = signal(false);

    daysOfWeek: DaysOfTheWeekType[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    weekdays: DaysOfTheWeekType[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    weekends: DaysOfTheWeekType[] = ['saturday', 'sunday'];

    availability: { [key: string]: { [key: string]: boolean } } = {
        weekday: {
            morning: true,
            overnight: true,
            evening: true
        },
        weekend: {
            morning: true,
            overnight: true,
            evening: true
        }
    };

    exclusionOptions = [
        {
            label: 'Mon',
            value: 'monday',
            checked: false
        },
        {
            label: 'Tue',
            value: 'tuesday',
            checked: false
        },
        {
            label: 'Wed',
            value: 'wednesday',
            checked: false
        },
        {
            label: 'Thu',
            value: 'thursday',
            checked: false
        },
        {
            label: 'Fri',
            value: 'friday',
            checked: false
        },
        {
            label: 'Sat',
            value: 'saturday',
            checked: false
        },
        {
            label: 'Sun',
            value: 'sunday',
            checked: false
        }
    ];

    constructor() {
        effect(() => {
            const schedule = this.proUserService.userSchedule();

            if (schedule) {
                this.setUpAvailabilityDisplay(schedule);
            }
        });
    }

    setUpAvailabilityDisplay(schedule: ProSchedule | null) {
        if (!schedule) return;

        this.sortSchedule(schedule);
        this.setUpExclusions(schedule);

        this.toggleAllShifts.set(
            this.daysOfWeek.every(
                (day: string) =>
                    schedule &&
                    schedule?.[`${day}_morning` as ProScheduleFields] === 1 &&
                    schedule?.[`${day}_evening` as ProScheduleFields] === 1 &&
                    schedule?.[`${day}_overnight` as ProScheduleFields] === 1
            )
        );
    }

    onScheduleToggle(
        event: ToggleCustomEvent,
        time: 'morning' | 'overnight' | 'evening' | 'all',
        type: 'weekday' | 'weekend' | 'all'
    ) {
        const checked = event.detail.checked;

        if (type === 'all') {
            this.toggleAllShifts.set(true);
            this.removeExclusions();

            Object.keys(this.availability).forEach(day => {
                this.setAllObjectKeyValues(true, this.availability[day]);
            });
        } else {
            if (!checked) {
                this.toggleAllShifts.set(false);
            }
            this.availability[type][time] = checked;
            this.toggleAllShifts.set(this.areAllShiftsAvailable());
        }
    }

    setAllObjectKeyValues(value: boolean, obj: { [key: string]: boolean }) {
        const keys = Object.keys(obj);
        keys.forEach(item => {
            obj[item] = value;
        });
    }

    areAllKeysValue(object: { [key: string]: boolean }, value: boolean) {
        const keys = Object.keys(object);
        return keys.every(key => object[key] === value);
    }

    areAllShiftsAvailable() {
        const availableWeekdays = this.areAllKeysValue(this.availability['weekday'], true);
        const availableWeekends = this.areAllKeysValue(this.availability['weekend'], true);
        const noExclusions = this.exclusionOptions.every(option => option.checked === false);
        return availableWeekdays && availableWeekends && noExclusions;
    }

    onExclusionChange() {
        this.toggleAllShifts.set(this.areAllShiftsAvailable());
    }

    removeExclusions() {
        this.toggleExclusion.set(false);

        this.exclusionOptions.forEach(day => (day.checked = false));
    }

    setUpExclusions(schedule: any) {
        if (!schedule) return;

        let hasExclusion = false;

        this.daysOfWeek.forEach(day => {
            const isExclusion =
                !schedule[day + '_morning'] && !schedule[day + '_overnight'] && !schedule[day + '_evening'];
            if (isExclusion) {
                const index = this.exclusionOptions.findIndex(exDay => exDay.value === day);
                if (index > -1) {
                    this.exclusionOptions[index].checked = true;
                    hasExclusion = true;
                }
            }
        });

        if (hasExclusion) {
            this.toggleExclusion.set(true);
        }
    }

    sortSchedule(schedule: any) {
        if (!schedule) return;

        this.availability['weekday']['morning'] = this.weekdays.some(day => schedule[day + '_morning'] === 1);
        this.availability['weekday']['evening'] = this.weekdays.some(day => schedule[day + '_evening'] === 1);
        this.availability['weekday']['overnight'] = this.weekdays.some(day => schedule[day + '_overnight'] === 1);

        this.availability['weekend']['morning'] = this.weekends.some(day => schedule[day + '_morning'] === 1);
        this.availability['weekend']['evening'] = this.weekends.some(day => schedule[day + '_evening'] === 1);
        this.availability['weekend']['overnight'] = this.weekends.some(day => schedule[day + '_overnight'] === 1);
    }

    hasSetExclusions() {
        return this.exclusionOptions.some(day => day.checked === true);
    }

    getExclusions() {
        const exclusions = this.exclusionOptions.filter(day => day.checked === true);

        return exclusions;
    }

    saveChanges() {
        let formObj: any = {};

        const hasExclusion = this.hasSetExclusions();
        const exclusions = this.getExclusions();

        this.daysOfWeek.forEach(day => {
            const isWeekEnd = this.weekends.indexOf(day) > -1;
            const dayTypeKey = isWeekEnd ? 'weekend' : 'weekday';
            const isAnExclusion = hasExclusion && exclusions.findIndex(exDay => exDay.value === day) > -1;

            if (!isAnExclusion) {
                if (this.availability[dayTypeKey]['morning']) {
                    formObj[day + '_morning'] = this.availability[dayTypeKey]['morning'] || true;
                }

                if (this.availability[dayTypeKey]['overnight']) {
                    formObj[day + '_overnight'] = this.availability[dayTypeKey]['overnight'] || true;
                }

                if (this.availability[dayTypeKey]['evening']) {
                    formObj[day + '_evening'] = this.availability[dayTypeKey]['evening'] || true;
                }
            }
        });

        this.loadingService.presentLoading();

        this.proUserService.updateAvailability(formObj).subscribe({
            next: data => {
                this.loadingService.dismissLoading();

                if (this.showSuccessToast) {
                    const options = { message: 'Updated availability', duration: 500 };
                    this.toastService.successToast(options);
                }
                this.proUserService.getUpdatedUser().subscribe();
                this.onSaveSuccess.emit();
            },
            error: Error => {
                this.loadingService.dismissLoading();
                const options = { message: 'Could not update availability', duration: 500 };
                this.toastService.errorToast(options);
                console.log('errors');
            }
        });
    }
}
